import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime, money } from "../lib/format";
import { ui } from "../lib/ui";

interface Cliente {
  id: number;
  razon_social: string;
  saldo_cta_cte: number;
}

interface Movimiento {
  id: number;
  fecha: string;
  tipo: string;
  debe: number;
  haber: number;
  saldo: number;
  comprobante_ref?: string | null;
  observaciones?: string | null;
}

export default function CtaCte() {
  const qc = useQueryClient();
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [cobrando, setCobrando] = useState(false);

  const { data: clientes } = useQuery({
    queryKey: ["clientes", "todos"],
    queryFn: async () => {
      const { data } = await api.get<Cliente[]>("/api/customers", {
        params: { limit: 500 },
      });
      return data;
    },
  });

  const cliente = useMemo(
    () => clientes?.find((c) => c.id === clienteId) ?? null,
    [clientes, clienteId]
  );

  const { data: movs, isLoading } = useQuery({
    queryKey: ["cta-cte", "cliente", clienteId],
    queryFn: async () => {
      const { data } = await api.get<Movimiento[]>(
        `/api/cta-cte/clientes/${clienteId}/movimientos`
      );
      return data;
    },
    enabled: !!clienteId,
  });

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Cuenta corriente</h1>
          <p className={ui.subtitle}>Movimientos por cliente</p>
        </div>
        <button
          className={ui.btnPrimary}
          disabled={!cliente}
          onClick={() => setCobrando(true)}
        >
          + Registrar cobro
        </button>
      </header>

      <div className={ui.page}>
        <div className="flex gap-3 mb-4 items-end">
          <div className="flex-1 max-w-md">
            <label className={ui.label}>Cliente</label>
            <select
              className={ui.input}
              value={clienteId ?? ""}
              onChange={(e) =>
                setClienteId(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">— Elegir cliente —</option>
              {clientes?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razon_social}
                </option>
              ))}
            </select>
          </div>
          {cliente && (
            <div className={ui.cardPad + " min-w-[200px]"}>
              <div className="text-xs text-slate-500 uppercase tracking-wide">
                Saldo actual
              </div>
              <div
                className={
                  "text-2xl font-bold " +
                  (Number(cliente.saldo_cta_cte) > 0
                    ? "text-rose-600"
                    : "text-slate-400")
                }
              >
                {money(cliente.saldo_cta_cte)}
              </div>
            </div>
          )}
        </div>

        {!clienteId && (
          <div className={ui.cardPad}>
            <p className={ui.empty}>Elegí un cliente para ver sus movimientos.</p>
          </div>
        )}

        {clienteId && (
          <div className={ui.card}>
            <table className={ui.table}>
              <thead className={ui.thead}>
                <tr>
                  <th className={ui.th}>Fecha</th>
                  <th className={ui.th}>Tipo</th>
                  <th className={ui.th}>Comprobante</th>
                  <th className={ui.th + " text-right"}>Debe</th>
                  <th className={ui.th + " text-right"}>Haber</th>
                  <th className={ui.th + " text-right"}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td colSpan={6} className="p-3">
                        <div className={ui.skeleton + " h-6 w-full"} />
                      </td>
                    </tr>
                  ))}
                {!isLoading &&
                  movs?.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className={ui.td}>{dateTime(m.fecha)}</td>
                      <td className={ui.td}>
                        <span
                          className={
                            ui.badge +
                            " " +
                            (m.tipo === "COBRO"
                              ? "bg-emerald-50 text-emerald-700"
                              : m.tipo === "VENTA"
                                ? "bg-blue-50 text-blue-700"
                                : "bg-slate-100 text-slate-600")
                          }
                        >
                          {m.tipo}
                        </span>
                      </td>
                      <td className={ui.td + " font-mono text-xs"}>
                        {m.comprobante_ref || "—"}
                      </td>
                      <td className={ui.td + " text-right"}>
                        {Number(m.debe) ? money(m.debe) : ""}
                      </td>
                      <td className={ui.td + " text-right"}>
                        {Number(m.haber) ? money(m.haber) : ""}
                      </td>
                      <td
                        className={ui.td + " text-right font-bold text-slate-900"}
                      >
                        {money(m.saldo)}
                      </td>
                    </tr>
                  ))}
                {!isLoading && !movs?.length && (
                  <tr>
                    <td colSpan={6} className={ui.empty}>
                      Sin movimientos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cobrando && cliente && (
        <CobroModal
          cliente={cliente}
          onClose={() => setCobrando(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["cta-cte"] });
            qc.invalidateQueries({ queryKey: ["clientes"] });
            setCobrando(false);
          }}
        />
      )}
    </div>
  );
}

function CobroModal({
  cliente,
  onClose,
  onSaved,
}: {
  cliente: Cliente;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [monto, setMonto] = useState<number>(0);
  const [medio, setMedio] = useState("EFECTIVO");
  const [ref, setRef] = useState("");
  const [obs, setObs] = useState("");

  const m = useMutation({
    mutationFn: async () => {
      await api.post("/api/cta-cte/clientes/cobro", {
        cliente_id: cliente.id,
        monto,
        medio_pago: medio,
        comprobante_ref: ref || undefined,
        observaciones: obs || undefined,
      });
    },
    onSuccess: onSaved,
  });

  return (
    <div className={ui.modalOverlay} onClick={onClose}>
      <div className={ui.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-slate-900 mb-1">
          Registrar cobro
        </h2>
        <p className="text-sm text-slate-500 mb-4">{cliente.razon_social}</p>
        <div className="space-y-3">
          <div>
            <label className={ui.label}>Monto</label>
            <input
              type="number"
              className={ui.input}
              value={monto}
              onChange={(e) => setMonto(parseFloat(e.target.value) || 0)}
              autoFocus
            />
          </div>
          <div>
            <label className={ui.label}>Medio de pago</label>
            <select
              className={ui.input}
              value={medio}
              onChange={(e) => setMedio(e.target.value)}
            >
              <option>EFECTIVO</option>
              <option>TRANSFERENCIA</option>
              <option>DEBITO</option>
              <option>CREDITO</option>
              <option>QR</option>
            </select>
          </div>
          <div>
            <label className={ui.label}>Comprobante</label>
            <input
              className={ui.input}
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </div>
          <div>
            <label className={ui.label}>Observaciones</label>
            <textarea
              className={ui.input}
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
          </div>
        </div>
        {m.isError && (
          <div className={ui.error + " mt-3"}>Error registrando cobro.</div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button className={ui.btnSecondary} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={ui.btnPrimary}
            disabled={m.isPending || monto <= 0}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Procesando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
